import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Fixed production backend — this Web frontend has NO backend of its own; it always talks to the
// shared production backend (same one the Mobile app + restaurant Manager use). Hardcoded on purpose
// so the custom domain (hallomagicpizza.ch) and every deploy route API calls to the correct backend,
// regardless of any REACT_APP_BACKEND_URL value the platform injects into the bundle at build time.
const PRODUCTION_BACKEND = "https://hallo-magic-test.emergent.host";

const API_ORIGIN = PRODUCTION_BACKEND;
const BASE = `${API_ORIGIN}/api`;

// Order source tag — Web app (matches Mobile which sends ios/android/web).
export const SOURCE = "web";

// SAFETY LOCK: while the permanent production backend is being sorted out, online ordering is
// disabled on the Web so no customer can receive a false "Commande reçue" for an order that would
// land in the wrong database. Flip to true (and re-point the backend URL) to re-enable ordering.
export const ORDERING_ENABLED = true;

// Product photos are stored as API-relative urls ("/api/files/..."); external urls (placeholders) pass through.
export function imgUri(url) {
  if (!url) return undefined;
  return url.startsWith("/") ? `${API_ORIGIN}${url}` : url;
}

// Optional customer session token (set by AuthProvider). Guests simply have no token.
let authToken = typeof localStorage !== "undefined" ? localStorage.getItem("customer_token") : null;
export const setAuthToken = (t) => { authToken = t; };

export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

async function request(path, init) {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = "";
    try {
      const j = await res.json();
      detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {}
    throw new ApiError(res.status, detail || `Erreur serveur (HTTP ${res.status})`);
  }
  return res.json();
}

export const api = {
  get: (p) => request(p),
  post: (p, body) => request(p, { method: "POST", body: JSON.stringify(body ?? {}) }),
  put: (p, body) => request(p, { method: "PUT", body: JSON.stringify(body ?? {}) }),
  del: (p) => request(p, { method: "DELETE" }),
};

// --- Customer account (Phase 2A/2B): login + account management ---
export const authApi = {
  login: (body) => api.post("/auth/login", body),          // POST /api/auth/login {phone, password}
  register: (body) => api.post("/auth/register", body),    // POST /api/auth/register
  me: () => api.get("/auth/me"),                            // GET /api/auth/me
  updateProfile: (body) => api.put("/auth/me", body),      // PUT /api/auth/me
  setMarketing: (consent) => api.put("/auth/me/marketing", { consent }), // PUT /api/auth/me/marketing
  addAddress: (body) => api.post("/auth/me/addresses", body),            // POST /api/auth/me/addresses
  updateAddress: (id, body) => api.put(`/auth/me/addresses/${id}`, body), // PUT /api/auth/me/addresses/{id}
  deleteAddress: (id) => api.del(`/auth/me/addresses/${id}`),            // DELETE /api/auth/me/addresses/{id}
};

// READ-ONLY: live menu/products/categories/extras/settings.
export function useMenu() {
  return useQuery({ queryKey: ["menu"], queryFn: () => api.get("/menu"), refetchInterval: 30000 });
}

// READ-ONLY: opening hours / available time slots for checkout time selection.
export function useOrderingStatus() {
  return useQuery({ queryKey: ["ordering"], queryFn: () => api.get("/settings/ordering"), refetchInterval: 60000 });
}

// READ-ONLY: the logged-in customer's order history.
export function useMyOrders(enabled) {
  return useQuery({ queryKey: ["orders", "account"], queryFn: () => api.get("/me/orders"), enabled, refetchInterval: 15000 });
}

// READ-ONLY: the logged-in customer's Fidelity (loyalty) progress. Guests must not call this.
export function useLoyalty(enabled) {
  return useQuery({
    queryKey: ["loyalty", "me"],
    queryFn: () => request("/me/loyalty", { cache: "no-store" }),
    enabled,
    refetchInterval: 30000,
  });
}

// Loyalty-quote items — EXACTLY the same shape the order payload sends (backend stays source of truth).
const loyaltyItems = (items) =>
  items.map((i) => ({
    product_id: i.product_id,
    quantity: i.quantity,
    size_key: i.size?.key ?? null,
    option_keys: i.options.map((o) => o.key),
    removed_ingredient_ids: i.removed_ingredients.map((r) => r.id),
    extras: i.extras.map((e) => ({ extra_id: e.extra_id, quantity: e.quantity })),
    note: i.note || null,
  }));

// READ-ONLY: preview the exact loyalty discount the backend will apply to this cart before confirming.
export function useLoyaltyQuote(items, enabled) {
  const payload = loyaltyItems(items);
  return useQuery({
    queryKey: ["loyalty", "quote", payload],
    queryFn: () => request("/loyalty/quote", { method: "POST", body: JSON.stringify({ items: payload }), cache: "no-store" }),
    enabled: enabled && payload.length > 0,
  });
}

// READ-ONLY: a single order by id, polled live every 4s so the status badge reflects the kitchen/Manager.
export function useOrder(id, initialData) {
  return useQuery({
    queryKey: ["order", id],
    queryFn: () => request(`/orders/${id}`, { cache: "no-store" }),
    enabled: !!id,
    initialData,
    refetchInterval: 4000,
    refetchIntervalInBackground: true,
  });
}

// Order POST with a hard timeout so a hung connection becomes a RETRIABLE timeout (AbortError)
// instead of an endless spinner.
async function postOrder(body, timeoutMs = 25000) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${BASE}/orders`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      let detail = "";
      try { const j = await res.json(); detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail); } catch {}
      throw new ApiError(res.status, detail || `Erreur serveur (HTTP ${res.status})`);
    }
    return res.json();
  } finally {
    clearTimeout(timer);
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// Retry ONLY when the outcome is uncertain: no response (network error / timeout → no status) or a
// gateway error (502/504). A definite backend rejection (4xx such as 409/422) is NEVER retried.
const isRetriable = (e) => !e?.status || e.status === 502 || e.status === 504;

// Real production order creation — payload built EXACTLY like the Mobile app (POST /api/orders).
// Duplicate-order protection: the caller supplies ONE client_order_id per checkout attempt and it is
// reused on every retry so the backend de-duplicates a request whose response was lost. Up to 3
// attempts; a new id is NEVER generated here.
export function usePlaceOrder() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (p) => {
      const body = {
        type: p.type,
        source: SOURCE,
        client_order_id: p.client_order_id, // idempotency key — identical across all retries
        items: p.items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          size_key: i.size?.key ?? null,
          option_keys: i.options.map((o) => o.key),
          removed_ingredient_ids: i.removed_ingredients.map((r) => r.id),
          extras: i.extras.map((e) => ({ extra_id: e.extra_id, quantity: e.quantity })),
          note: i.note || null,
        })),
        customer: p.customer,
        address: p.address,
        requested_time: p.requested_time,
        requested_date: p.requested_date ?? null,
        general_note: p.general_note,
        age_confirmed: p.age_confirmed,
        save_address: !!p.save_address,
        language: p.language,
        payment_method: p.payment_method,
        marketing_consent: !!p.marketing_consent,
      };
      const MAX_ATTEMPTS = 3;
      let lastErr;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          return await postOrder(body);
        } catch (e) {
          lastErr = e;
          if (!isRetriable(e) || attempt === MAX_ATTEMPTS) throw e;
          await sleep(attempt * 1000); // brief backoff before retrying the SAME client_order_id
        }
      }
      throw lastErr;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}
